import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  Chip,
  alpha
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import api from '../../../services/api'
import { useSnackbar } from 'notistack'

const PatientSearchDialog = ({ open, onClose, onSelectPatient }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [searchTerm, setSearchTerm] = useState('')
  const [patients, setPatients] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchPerformed, setSearchPerformed] = useState(false)

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      enqueueSnackbar('Please enter search term', { variant: 'warning' })
      return
    }

    setLoading(true)
    try {
      const response = await api.get(`/indoor-registeration/list?name=${searchTerm}`)
      
      const activePatients = response.data.data.filter(
        patient => patient.admission_status === 'admit' || patient.admission_status === 'discharged'
      )
      
      setPatients(activePatients)
      setSearchPerformed(true)
      
      if (activePatients.length === 0) {
        enqueueSnackbar('No patients found', { variant: 'info' })
      }
    } catch (error) {
      console.error('Search failed:', error)
      enqueueSnackbar('Search failed', { variant: 'error' })
      setPatients([])
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (patient) => {
    onSelectPatient(patient)
    onClose()
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        py: 2
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonSearchIcon />
          <Typography variant="h6">
            Search Patient for MRD File
          </Typography>
        </Box>
        
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="medium"
            placeholder="Search by UHID, Patient Name, or Mobile Number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              sx: { 
                bgcolor: 'white',
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'white' // Keep white on hover
                }
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={handleSearch} 
                    edge="end"
                    sx={{ 
                      bgcolor: 'primary.dark',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)' // Keep original border color on hover
                  }
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 1
                  }
                }
              }
            }}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: 200 
          }}>
            <CircularProgress />
          </Box>
        ) : patients.length > 0 ? (
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              borderTop: 1, 
              borderColor: 'divider',
              maxHeight: 400
            }}
          >
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#1976d2', 0.05) }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>UHID</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Patient Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>IPD No</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Admission Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((patient) => (
                  <TableRow 
                    key={patient._id} 
                    hover
                    sx={{ 
                      '&:nth-of-type(even)': { bgcolor: alpha('#1976d2', 0.02) },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {patient.uhid}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2">
                        {patient.patient?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {patient.ipd_no}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2">
                        {new Date(patient.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={patient.admission_status.toUpperCase()}
                        size="small"
                        color={patient.admission_status === 'admit' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSelect(patient)}
                        sx={{ 
                          borderRadius: 1,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : searchPerformed ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            color: 'text.secondary'
          }}>
            <PersonSearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              No patients found
            </Typography>
            <Typography variant="body2">
              Try searching with different terms
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            color: 'text.secondary'
          }}>
            <PersonSearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              Search for Patients
            </Typography>
            <Typography variant="body2">
              Enter UHID, patient name, or mobile number to search
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 1,
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PatientSearchDialog